import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import { useHeatsRowFilter } from "./context/HeatsRowFilterContext";
import { HeatFilterField, isColumnFiltered } from "./heatsRowFilter";

interface HeatHeaderFilterProps {
  field: HeatFilterField;
  label: string;
  options: string[];
}

export const HeatHeaderFilter = ({
  field,
  label,
  options,
}: HeatHeaderFilterProps) => {
  const { t } = useTenantTranslation();
  const { selection, setFieldValues } = useHeatsRowFilter();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const values = selection[field];
  const isFiltered = isColumnFiltered(values);

  const toggle = (option: string) => {
    setFieldValues(
      field,
      values.includes(option)
        ? values.filter((value) => value !== option)
        : [...values, option]
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        minWidth: 0,
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: "fontWeightMedium" }}
      >
        {label}
      </Typography>
      <IconButton
        aria-label={`${label} ${t("filter")}`}
        sx={{ p: 0.25 }}
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
      >
        {isFiltered ? (
          <FilterAltIcon sx={{ fontSize: 16 }} color="primary" />
        ) : (
          <FilterAltOutlinedIcon
            sx={{ fontSize: 16, color: "text.secondary" }}
          />
        )}
      </IconButton>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <List dense sx={{ maxHeight: 320, minWidth: 180, overflowY: "auto" }}>
          {options.map((option) => (
            <ListItemButton
              key={option}
              dense
              onClick={() => {
                toggle(option);
              }}
            >
              <Checkbox
                size="small"
                edge="start"
                disableRipple
                checked={values.includes(option)}
              />
              <ListItemText primary={option} />
            </ListItemButton>
          ))}
        </List>
        {isFiltered ? (
          <Box sx={{ borderTop: 1, borderColor: "grey.300", p: 0.5 }}>
            <Button
              size="small"
              fullWidth
              onClick={() => {
                setFieldValues(field, []);
              }}
            >
              {t("clear")}
            </Button>
          </Box>
        ) : null}
      </Popover>
    </Box>
  );
};
